/**
 * Small helpers used across admin pages — INR formatting, SLA pill,
 * status pill.
 */
import React from "react";

export const STATUS_LABEL = {
  submitted: "Submitted",
  under_review: "Under review",
  quote_sent: "Quote sent",
  accepted: "Accepted",
  declined: "Declined",
  expired: "Expired",
  confirmed_offline: "Confirmed offline",
  conversation_ongoing: "Conversation",
};

export const STATUS_STYLES = {
  submitted: "bg-[#0F172A]/5 text-[#0F172A]/70",
  under_review: "bg-[#4A6FA5]/10 text-[#4A6FA5]",
  quote_sent: "bg-[#C8A24B]/15 text-[#9A7A2F]",
  accepted: "bg-[#4F8060]/10 text-[#4F8060]",
  declined: "bg-[#B0413E]/10 text-[#B0413E]",
  expired: "bg-[#0F172A]/5 text-[#0F172A]/45",
  confirmed_offline: "bg-[#4F8060]/15 text-[#3B5F47]",
  conversation_ongoing: "bg-[#4A6FA5]/10 text-[#4A6FA5]",
};

export function formatINR(n) {
  if (n == null) return "–";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(n);
}

export function formatINRWithDecimals(n) {
  if (n == null) return "–";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(n);
}

export function StatusPill({ status, className = "" }) {
  const label = STATUS_LABEL[status] || (status || "—").replaceAll("_", " ");
  const style = STATUS_STYLES[status] || STATUS_STYLES.submitted;
  return (
    <span
      data-testid={`status-pill-${status}`}
      className={[
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] uppercase tracking-[0.22em]",
        style,
        className,
      ].join(" ")}
    >
      <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-current" />
      {label}
    </span>
  );
}

export function SlaPill({ remaining, met, className = "" }) {
  if (met) {
    return (
      <span
        data-testid="sla-pill-met"
        className={`inline-flex items-center gap-1.5 rounded-full bg-[#4F8060]/10 px-2.5 py-1 text-[10px] uppercase tracking-[0.22em] text-[#3B5F47] ${className}`}
      >
        SLA met
      </span>
    );
  }
  const hours = remaining != null ? Math.floor(remaining / 3600) : null;
  let tone = "bg-[#4F8060]/10 text-[#3B5F47]";
  if (hours != null) {
    if (hours < 2) tone = "bg-[#B0413E]/10 text-[#B0413E]";
    else if (hours < 8) tone = "bg-[#C8A24B]/15 text-[#9A7A2F]";
  }
  return (
    <span
      data-testid="sla-pill"
      className={[
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] uppercase tracking-[0.22em]",
        tone,
        className,
      ].join(" ")}
    >
      {remaining == null
        ? "—"
        : `${hours}h ${Math.floor((remaining % 3600) / 60)
            .toString()
            .padStart(2, "0")}m`}
    </span>
  );
}

export function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(iso);
  }
}
